import type { ChipProps } from "@mui/material/Chip";

// antd <Tag color> accepts either a MUI-palette-ish *status* name, a *preset hue*
// name, or a raw hex/CSS color. MUI <Chip color> only accepts palette keys, so we
// split: status names -> Chip `color` prop; hue names / hex -> outlined Chip tinted
// via sx. Pure + unit-tested so the 38 call sites become safe import-only swaps.

type MuiChipColor = NonNullable<ChipProps["color"]>;

const STATUS_TO_CHIP: Record<string, MuiChipColor> = {
  success: "success",
  error: "error",
  warning: "warning",
  processing: "info",
  default: "default",
};

// Preset hues that antd rendered as a SOLID coloured pill with white text (not an
// outlined tint, and NOT the theme palette). The legacy `.ant-tag--blue` was
// $blue-500; mapping it to MUI `color="info"` turned the filter chips a muted teal.
const SOLID_HUE_HEX: Record<string, string> = {
  blue: "#0091f9", // legacy $blue-500 — the filter chips (Active/Subscribed/Watch List)
};

// antd v5 preset hue hexes (for names that have no MUI palette equivalent).
const ANTD_HUE_HEX: Record<string, string> = {
  red: "#f5222d",
  volcano: "#fa541c",
  orange: "#fa8c16",
  gold: "#faad14",
  yellow: "#fadb14",
  lime: "#a0d911",
  green: "#52c41a",
  cyan: "#13c2c2",
  geekblue: "#2f54eb",
  purple: "#722ed1",
  magenta: "#eb2f96",
};

export interface TagColorStyle {
  color?: MuiChipColor;
  variant: NonNullable<ChipProps["variant"]>;
  // string for a flat tint, or a nested object for pseudo/child selectors (e.g. the
  // delete icon on the solid blue chip) — both valid MUI sx entries.
  sx?: Record<string, string | Record<string, string>>;
}

const isHexOrCss = (c: string) => /^#|^rgb|^hsl/.test(c);

// Resolve an antd Tag `color` value into MUI Chip props.
export const resolveTagColor = (color?: string): TagColorStyle => {
  if (!color) return { color: "default", variant: "filled" };
  const key = color.toLowerCase();
  if (STATUS_TO_CHIP[key]) return { color: STATUS_TO_CHIP[key], variant: "filled" };
  if (SOLID_HUE_HEX[key]) {
    const bg = SOLID_HUE_HEX[key];
    return {
      variant: "filled",
      sx: {
        backgroundColor: bg,
        color: "#fff",
        "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" },
        "& .MuiChip-deleteIcon:hover": { color: "#fff" },
      },
    };
  }
  const hex = ANTD_HUE_HEX[key] ?? (isHexOrCss(color) ? color : undefined);
  if (hex) {
    return { variant: "outlined", sx: { color: hex, borderColor: hex } };
  }
  // unknown name -> neutral default rather than throwing (faithful to antd's lenient behavior)
  return { color: "default", variant: "filled" };
};
